import { type SortingState } from "@tanstack/react-table";

/**
 * Merges user's stored column order with current columns, preserving user preferences
 * while ensuring new columns added by developers are included.
 *
 * @param storedOrder - User's column order from localStorage (may be undefined)
 * @param defaultOrder - Current column IDs from the table definition
 * @returns Merged column order with existing preferences preserved and new columns appended
 */
export function mergeColumnOrder(storedOrder: string[] | undefined, defaultOrder: string[]): string[] {
  if (!storedOrder) return defaultOrder;

  const currentColumnIds = new Set(defaultOrder);
  const existingColumns = storedOrder.filter((id) => currentColumnIds.has(id));
  const newColumns = defaultOrder.filter((id) => !storedOrder.includes(id));

  return [...existingColumns, ...newColumns];
}

/**
 * Type guard to check if a string is a valid sort field.
 *
 * @param field - The field to validate
 * @param validFields - Array of valid field values
 * @returns True if field is in validFields
 */
export function isValidSortField<T extends string>(field: string | undefined, validFields: readonly T[]): field is T {
  return field !== undefined && validFields.includes(field as T);
}

/**
 * Convert TanStack Table sorting state to GraphQL sortBy format.
 *
 * @param sorting - TanStack Table sorting state
 * @param fallback - Default sort to use if sorting is empty
 * @returns Array of sort objects for GraphQL query
 */
export function createSortByArray<TColumnId extends string>(
  sorting: SortingState,
  fallback?: { id: TColumnId; desc: boolean },
): Array<{ field: TColumnId; desc: boolean }> {
  if (sorting.length === 0 && fallback) {
    return [{ field: fallback.id, desc: fallback.desc }];
  }
  return sorting.map((s) => ({ field: s.id as TColumnId, desc: s.desc }));
}
